import { inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Warning } from "@phosphor-icons/react";

/**
 * TDS challan tab — inputs for section / period / AY / challan no / BSR / CIN
 * plus a live journal-preview summary. Extracted from ChallanPostDialog to
 * keep each component focused (<100 lines).
 */
export default function ChallanTdsPanel({
    debit, tdsSection, setTdsSection,
    tdsPeriod, setTdsPeriod,
    ay, setAy,
    challanNo, setChallanNo,
    bsrCode, setBsrCode,
    cin, setCin,
    tdsSectionOptions, selectedTdsSectionInfo,
}) {
    const overpay = selectedTdsSectionInfo
        && debit > selectedTdsSectionInfo.outstanding + 0.5;
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <FieldBlock label="Section">
                    <Select value={tdsSection} onValueChange={setTdsSection}>
                        <SelectTrigger className="h-9" data-testid="challan-tds-section"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {tdsSectionOptions.map((s) => (
                                <SelectItem key={s.section} value={s.section}>
                                    {s.section} — outstanding ₹{inr(s.outstanding)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {selectedTdsSectionInfo ? (
                        <div className="text-[11px] text-stone-500 mt-1">
                            {selectedTdsSectionInfo.label} — {selectedTdsSectionInfo.outstanding > 0
                                ? `owed ₹${inr(selectedTdsSectionInfo.outstanding)}`
                                : "no outstanding balance"}
                        </div>
                    ) : null}
                </FieldBlock>
                <FieldBlock label="Deducted for month">
                    <Input type="month" value={tdsPeriod} onChange={(e) => setTdsPeriod(e.target.value)}
                           className="h-9" data-testid="challan-tds-period" />
                </FieldBlock>
            </div>
            <div className="grid grid-cols-3 gap-3">
                <FieldBlock label="Assessment year">
                    <Input value={ay} onChange={(e) => setAy(e.target.value)}
                           placeholder="2026-27" className="h-9 font-mono" data-testid="challan-tds-ay" />
                </FieldBlock>
                <FieldBlock label="Challan no.">
                    <Input value={challanNo} onChange={(e) => setChallanNo(e.target.value)}
                           className="h-9 font-mono" data-testid="challan-tds-no" />
                </FieldBlock>
                <FieldBlock label="BSR code">
                    <Input value={bsrCode} onChange={(e) => setBsrCode(e.target.value)}
                           className="h-9 font-mono" data-testid="challan-tds-bsr" />
                </FieldBlock>
            </div>
            <FieldBlock label="CIN">
                <Input value={cin} onChange={(e) => setCin(e.target.value)}
                       placeholder="Challan Identification No. (from receipt)"
                       className="h-9 font-mono" data-testid="challan-tds-cin" />
            </FieldBlock>
            <div className="rounded-md p-3 text-sm space-y-1 border"
                 style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}
                 data-testid="challan-tds-summary">
                <div className="flex justify-between">
                    <span className="text-stone-500">Journal entry</span>
                    <span className="font-medium">Dr TDS Payable ({tdsSection}) · Cr Bank</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-stone-500">Amount</span>
                    <span className="se-num font-medium">₹ {inr(debit)}</span>
                </div>
                {overpay ? (
                    <div className="text-[11px] text-amber-700 mt-1">
                        <Warning size={11} className="inline mr-1" />
                        Payment exceeds outstanding by ₹{inr(debit - selectedTdsSectionInfo.outstanding)} —
                        make sure you picked the right section, else the excess will show as a debit balance.
                    </div>
                ) : null}
            </div>
        </div>
    );
}


function FieldBlock({ label, children }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
            {children}
        </div>
    );
}
